import React, { useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Typography, InputBase, Tooltip } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { createActions } from '../../store';
import { splitFileExtension } from '../../../../../utilities/stringUtilities';

const MAX_FILE_NAME_LENGTH = 150;
const NO_AUDIO_TITLE = 'No audio file selected';

const styles = theme => ({
    nameButton: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        padding: '0 ' + theme.spacing(4).toString(),
        cursor: 'pointer'
    },
    nameText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'left'
    },
    nameInputWrapper: {
        padding: '0 ' + theme.spacing(4).toString(),
        minWidth: 0
    },
    nameInput: {
        ...theme.typography.subtitle1,
        width: 'min(420px, 40vw)',
        borderBottom: '1px solid ' + theme.palette.primary.main
    },
    noAudioTitle: {
        padding: '0 ' + theme.spacing(4).toString(),
        fontStyle: 'italic',
        whiteSpace: 'nowrap'
    }
});

const EditableAudioFileName = ({ classes, displayFileName, savingInProgress, renameAudioFile }) => {
    const [editing, setEditing] = useState(false);
    const [draftName, setDraftName] = useState('');

    // Escape and Enter finish the edit themselves; the blur that follows must not commit it a second time.
    const editFinishedRef = useRef(false);

    const startEditing = useCallback(() => {
        if (savingInProgress)
            return;

        editFinishedRef.current = false;
        setDraftName(splitFileExtension(displayFileName).baseName);
        setEditing(true);
    }, [savingInProgress, displayFileName]);

    const finishEditing = useCallback((shouldCommit) => {
        if (editFinishedRef.current)
            return;

        editFinishedRef.current = true;
        setEditing(false);

        // The extension is not editable: it is taken off while editing and put back here, so the name
        // always keeps describing the file that is actually stored.
        // An empty name would leave the project untitled, so it is treated as a cancel.
        const newBaseName = draftName.trim();
        const newName = newBaseName + splitFileExtension(displayFileName).extension;
        if (shouldCommit && newBaseName && newName !== displayFileName)
            renameAudioFile(newName);
    }, [draftName, displayFileName, renameAudioFile]);

    const onInputKeyDown = useCallback((event) => {
        if (event.key === 'Enter')
            finishEditing(true);
        else if (event.key === 'Escape')
            finishEditing(false);

        // Ctrl+Z / Ctrl+Y belong to the text field while it has focus, not to the editor's history.
        const isHistoryShortcut = (event.ctrlKey || event.metaKey) && ['z', 'y'].includes(event.key.toLowerCase());
        if (isHistoryShortcut)
            event.stopPropagation();
    }, [finishEditing]);

    const onNameKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            startEditing();
        }
    }, [startEditing]);

    if (!displayFileName) {
        return (
            <Typography variant='subtitle1' color='textSecondary' className={classes.noAudioTitle}>
                {NO_AUDIO_TITLE}
            </Typography>
        );
    }

    if (editing) {
        return (
            <div className={classes.nameInputWrapper}>
                <InputBase
                    autoFocus
                    value={draftName}
                    onChange={(event) => setDraftName(event.target.value)}
                    onKeyDown={onInputKeyDown}
                    onBlur={() => finishEditing(true)}
                    onFocus={(event) => event.target.select()}
                    inputProps={{ maxLength: MAX_FILE_NAME_LENGTH, 'aria-label': 'Audio file name' }}
                    className={classes.nameInput}
                />
            </div>
        );
    }

    return (
        <Tooltip title={savingInProgress ? displayFileName : 'Click to rename'} arrow placement='bottom'>
            <div
                className={classes.nameButton}
                role='button'
                tabIndex={0}
                aria-label={`Rename audio file ${displayFileName}`}
                onClick={startEditing}
                onKeyDown={onNameKeyDown}
            >
                <Typography variant='subtitle1' color='textSecondary' className={classes.nameText}>
                    {displayFileName}
                </Typography>
            </div>
        </Tooltip>
    );
};

const mapStateToProps = (state) => {
    const { displayFileName } = state.create.audioSettings;
    const { savingInProgress } = state.create.videoSavingState;
    return { displayFileName, savingInProgress };
};

const mapDispatchToProps = (dispatch) => ({
    renameAudioFile: (displayFileName) => dispatch(createActions.renameAudioFile(displayFileName))
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(EditableAudioFileName, styles));
